// What the web app is showing and doing: the view (scope + filter), the reading
// session, rows read during this view, optimistic read/save changes, toasts.
import { onSessionReset } from "./session";
import { useSyncExternalStore } from "react";
import { extendSession, remainingSession, scopeKey, type ArticleRow, type Filter, type Scope } from "@vladrss/core";
import { keep, keptAt, noteNow } from "./kept";
import { extend, NO_PICKS, step, toggle, type Picks } from "./picks";
import { fromLocation, toPath } from "./route";
import { asTransition } from "./transition";

export type Row = ArticleRow & { body: "none" | "feed" | "partial" | "full"; hoverText?: string };

export interface Toast { id: number; text: string; undo?: () => void; icon?: string; /** what the undo button says ("Undo" unless given) */ action?: string }

export type SettingsSection = "sources" | "filters" | "library" | "reading" | "appearance";

interface State {
  scope: Scope;
  filter: Filter;
  /** ids read while this view was showing; they stay visible under Unread until the view changes */
  keep: Set<string>;
  /** optimistic changes not yet confirmed by the server */
  overlay: Map<string, { isRead?: boolean; isSaved?: boolean }>;
  /** Authoritative deletions must also stay out of paused list snapshots. */
  removed: Map<string, "article" | "source">;
  /** the open article and the frozen order it is read in */
  reading: { id: string; session: string[]; label: string; visit?: number } | null;
  focus: boolean;
  /** the row highlighted in the list (j/k before reading) */
  selected: string | null;
  toast: Toast | null;
  modal: null | "settings" | "palette" | "help";
  /** which Settings section is showing, and whether Sources should open on Add */
  settingsSection: SettingsSection;
  sourcesAdd: boolean;
  /** the list row under the pointer, fetched ahead so opening it is instant */
  peek: string | null;
  /** rows picked with Ctrl/⌘-click, Shift-click or Shift+↑/↓, for marking many at once (lib/picks.ts) */
  picks: Picks;
  /** the open article, while it waits out READ_AFTER before counting as read (shown as read already) */
  pendingRead: string | null;
}

/** How long an article must be open before it counts as read: a mistaken click or a quick step past leaves it unread. */
export const READ_AFTER = 500;

// the app opens where the address says (a refresh lands where you were)
const start = typeof location === "undefined" ? null : fromLocation();
const SECTIONS: readonly SettingsSection[] = ["sources", "filters", "library", "reading", "appearance"];
const sectionOf = (v?: string) => SECTIONS.find((x) => x === v);
let s: State = {
  // the open article stays in its list even when it is already read, so reading goes on from it
  scope: start?.scope ?? { type: "all" }, filter: start?.filter ?? "unread", keep: new Set(start?.article ? [start.article] : []), overlay: new Map(), removed: new Map(),
  reading: start?.article ? { id: start.article, session: [start.article], label: "" } : null,
  focus: start?.focus ?? false, selected: start?.article ?? null, toast: null, modal: sectionOf(start?.settings) ? "settings" : null, settingsSection: sectionOf(start?.settings) ?? "sources", sourcesAdd: false, peek: null, picks: NO_PICKS, pendingRead: null,
};
/**
 * The article the page opened on, and whether it was reached afresh (a link, a bookmark, a typed
 * address) rather than by a refresh or Back/Forward: a fresh visit counts as opening it, so it is
 * read like any other; a refresh leaves it as it was (kept unread, as a rule). Asked once.
 */
const visit = typeof performance === "undefined" ? undefined : (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
export const landing = { id: start?.article ?? null, fresh: visit === undefined || visit === "navigate" };

/** The article a refresh landed on: it goes back to where this tab had it, once. Every other article opens at its top. */
export const backToPlace = { id: start?.article ?? null };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
export const getState = () => s;
/** Calls back on every change; returns how to stop. */
export function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
export function useStore<T>(pick: (st: State) => T): T {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => pick(s));
}
let viewContext = 0;
let replacingView = false;
export const currentViewContext = () => viewContext;
export function update(patch: Partial<State>) {
  if ((patch.scope && scopeKey(patch.scope) !== scopeKey(s.scope)) || (patch.filter && patch.filter !== s.filter)) viewContext++;
  s = { ...s, ...patch }; emit();
}

/** One Settings screen; callers say which section to land on. */
export const openSettings = (section: SettingsSection = "sources", add = false) => update({ modal: "settings", settingsSection: section, sourcesAdd: add });

let shown: string[] = [];
const articleOwners = new Map<string, number>();
export function retainArticle(id: string) {
  articleOwners.set(id, (articleOwners.get(id) ?? 0) + 1);
  return () => { const n = (articleOwners.get(id) ?? 1) - 1; if (n) articleOwners.set(id, n); else articleOwners.delete(id); };
}
const snapshots = new Map<string, Row[]>();
const SNAPSHOT_LIMIT = 8, SNAPSHOT_ROWS = 300, ROW_LIMIT = 2500;
export function viewSnapshot(key: string) { return snapshots.get(key); }
export function retainView(key: string, rows: Row[]) {
  snapshots.delete(key); snapshots.set(key, rows.slice(0, SNAPSHOT_ROWS));
  while (snapshots.size > SNAPSHOT_LIMIT) snapshots.delete(snapshots.keys().next().value!);
  pruneRetained();
}
export function visibleRows(ids: readonly string[]) { shown = [...ids]; pruneRetained(); }
function pruneRetained() {
  const pinned = new Set([...shown, ...articleOwners.keys(), ...s.keep, ...(s.reading?.session ?? []), ...s.overlay.keys(), ...s.picks.picked]);
  for (const id of [s.selected, s.peek, s.pendingRead]) if (id) pinned.add(id);
  for (const rows of snapshots.values()) for (const row of rows) pinned.add(row.id);
  for (const [id] of rowCache) {
    if (rowCache.size <= ROW_LIMIT) break;
    if (!pinned.has(id)) { rowCache.delete(id); confirmed.delete(id); }
  }
  // Tombstones protect every retained snapshot. Once all owners release an id it can retire too.
  const removed = new Map([...s.removed].filter(([id]) => pinned.has(id)));
  if (removed.size !== s.removed.size) update({ removed });
  for (const id of confirmed.keys()) if (!rowCache.has(id) && !s.overlay.has(id)) confirmed.delete(id);
}
let visitSequence = 0;
export function openReading(id: string, session: string[], label: string) {
  update({ reading: { id, session: session.includes(id) ? session : [id], label, visit: ++visitSequence }, selected: id });
}
export function extendReading(ids: string[], page: boolean, label: string) {
  const r = s.reading;
  if (!r) return;
  const session = extendSession(r.session, ids, r.session.length === 1 ? "hydrate" : page ? "page" : "live");
  if (session.length !== r.session.length || session.some((id, i) => id !== r.session[i])) update({ reading: { ...r, session, label } });
}
export function closeReading() { update({ reading: null, focus: false, selected: s.reading?.id ?? s.selected }); }

/** Rows seen from any query, so read-during-view rows can still render after leaving the unread results. */
export const rowCache = new Map<string, Row>();
export function remember(rows: readonly Row[], activeSources?: ReadonlySet<string>) {
  // A late save can survive unfollow. An explicit article deletion cannot be
  // undone by an older list snapshot, even when that snapshot says "saved".
  rows = rows.filter((r) => !s.removed.has(r.id) || (s.removed.get(r.id) === "source" && (r.isSaved || (r.retiredAt === undefined && activeSources?.has(r.feedId)))));
  const revived = rows.filter((r) => s.removed.has(r.id));
  for (const r of rows) rowCache.set(r.id, r);
  pruneRetained();
  // A saved row may arrive after its source's deletion. A real query answer wins.
  if (revived.length) {
    const removed = new Map(s.removed);
    for (const r of revived) removed.delete(r.id);
    update({ removed });
  }
}

/** A row with pending optimistic changes applied. */
export function withOverlay(r: Row): Row {
  const o = s.overlay.get(r.id);
  return o ? { ...r, ...(o.isSaved === true ? { retiredAt: undefined } : {}), ...(o.isRead !== undefined ? { isRead: o.isRead } : {}), ...(o.isSaved !== undefined ? { isSaved: o.isSaved } : {}) } : r;
}

type ArticleChange = { isRead?: boolean; isSaved?: boolean };
interface ArticleEdit { sequence: number; id: string; change: ArticleChange }
let editSequence = 0;
const pending = new Map<number, ArticleEdit>();
const confirmed = new Map<string, Partial<Record<keyof ArticleChange, number>>>();
function showPending() {
  const overlay = new Map<string, ArticleChange>();
  for (const e of pending.values()) {
    const change = { ...overlay.get(e.id) };
    for (const field of ["isRead", "isSaved"] as const) if (e.change[field] !== undefined && e.sequence > (confirmed.get(e.id)?.[field] ?? 0)) change[field] = e.change[field];
    if (Object.keys(change).length) overlay.set(e.id, change);
  }
  update({ overlay });
}
/** Retire requests, never matching values: Read → Unread → Read is three requests. */
export function beginArticleEdit(id: string, change: ArticleChange): ArticleEdit {
  const edit = { sequence: ++editSequence, id, change };
  pending.set(edit.sequence, edit); showPending(); return edit;
}
export function finishArticleEdit(edit: ArticleEdit, success: boolean) {
  if (!pending.delete(edit.sequence)) return;
  if (success) {
    const seen = confirmed.get(edit.id) ?? {}, row = rowCache.get(edit.id), patch: ArticleChange = {};
    for (const field of ["isRead", "isSaved"] as const) if (edit.change[field] !== undefined && edit.sequence > (seen[field] ?? 0)) {
      seen[field] = edit.sequence;
      patch[field] = edit.change[field];
    }
    confirmed.set(edit.id, seen);
    if (row) rowCache.set(edit.id, { ...row, ...patch });
  }
  showPending();
}

/** Retire only known deletions, never absence from a filtered/paginated query. */
export function forgetArticles(ids: readonly string[], reason: "article" | "source" = "article") {
  ids = ids.filter((id) => !s.removed.has(id) || (reason === "article" && s.removed.get(id) !== "article") || rowCache.has(id) || s.keep.has(id) || s.reading?.session.includes(id) || s.selected === id || s.peek === id || s.pendingRead === id || s.overlay.has(id) || s.picks.picked.has(id));
  if (!ids.length) return;
  const gone = new Set(ids), removed = new Map(s.removed);
  for (const id of gone) { rowCache.delete(id); confirmed.delete(id); removed.set(id, s.removed.get(id) === "article" ? "article" : reason); }
  for (const [key, edit] of pending) if (gone.has(edit.id)) pending.delete(key);
  let reading = s.reading;
  if (reading) {
    const next = remainingSession(reading.id, reading.session, gone);
    reading = next ? { ...reading, ...next } : null;
  }
  const surviving = (id: string | null) => id && !gone.has(id) ? id : null;
  const picked = new Set([...s.picks.picked].filter((id) => !gone.has(id)));
  const overlay = new Map(s.overlay);
  for (const id of gone) overlay.delete(id);
  if (backToPlace.id && gone.has(backToPlace.id)) backToPlace.id = null;
  update({
    removed, overlay, reading, keep: new Set([...s.keep].filter((id) => !gone.has(id))),
    selected: s.selected && gone.has(s.selected) ? reading?.id ?? null : s.selected,
    peek: surviving(s.peek), pendingRead: surviving(s.pendingRead), focus: reading ? s.focus : false,
    picks: picked.size ? { picked, anchor: surviving(s.picks.anchor), end: surviving(s.picks.end), base: new Set([...s.picks.base].filter((id) => !gone.has(id))) } : NO_PICKS,
  });
}

/** Unfollow keeps saved articles. Paused/unknown source queries do not call this. */
export function reconcileSources(feeds: readonly { id: string; deleted: boolean }[]) {
  const live = new Set(feeds.filter((f) => !f.deleted).map((f) => f.id));
  forgetArticles([...rowCache.values()].filter((r) => !live.has(r.feedId) && !withOverlay(r).isSaved).map((r) => r.id), "source");
}

// ---------- view memory: scroll and selection per view ----------
export const viewKey = (st = s, extra = "") => `${scopeKey(st.scope)}|${st.filter}|${extra}`;
// kept for the tab, so a refresh lands at the same place in the list too (lib/kept.ts)
export const recall = (key: string) => keptAt(`list:${key}`);
export const memorize = (key: string, top: number, selected: string | null, anchor?: string, offset?: number) => keep(`list:${key}`, { top, selected, anchor, offset });

/** A confirmed rename changes the current view's identity, without navigating away. */
export function retargetView(context: number, scope: Scope, label: string) {
  if (context !== viewContext) return false;
  noteNow();
  for (const filter of ["unread", "all", "saved"] as const) {
    const old = keptAt(`list:${viewKey({ ...s, filter })}`);
    if (old) keep(`list:${viewKey({ ...s, scope, filter })}`, { ...old });
  }
  replacingView = true;
  try { update({ scope, reading: s.reading ? { ...s.reading, label } : null }); }
  finally { replacingView = false; }
  return true;
}

export function setView(scope: Scope, filter?: Filter) {
  viewContext++; // even choosing the same view is a new navigation intent
  // a view chosen anew opens on what is left to read: showing read articles, like Saved, is for
  // the view it was asked of and never follows you to the next
  const nextFilter = filter ?? "unread";
  // a view chosen anew starts at its top (Back/Forward and a refresh return to where it was);
  // choosing the one already showing takes it back to its top
  noteNow(); // which row the list leaves at its top, for Back
  const next = viewKey({ ...s, scope, filter: nextFilter });
  keep(`list:${next}`, { top: 0 });
  if (next === viewKey()) dispatchEvent(new Event("vladrss:list-top"));
  update({ scope, filter: nextFilter, keep: new Set(), reading: null, focus: false, selected: null });
}

let toastSeq = 0;
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(text: string, undo?: () => void, icon?: string, action?: string) {
  clearTimeout(toastTimer);
  update({ toast: { id: ++toastSeq, text, undo, icon, action } });
  toastTimer = setTimeout(() => update({ toast: null }), undo ? 6000 : 3200);
}

let peekTimer: ReturnType<typeof setTimeout> | undefined;
/** Pointing at a row for a moment starts loading it. */
export function peekAt(id: string) { clearTimeout(peekTimer); peekTimer = setTimeout(() => { if (s.peek !== id) update({ peek: id }); }, 120); }

/** The rows the list shows, in the order it shows them. */
const shownIds = () => shown;

/** Ctrl/⌘-click picks or lets go of one row; Shift-click picks from the anchor to here, as in a spreadsheet. */
export function pick(id: string, range: boolean) {
  update({ picks: range ? extend(s.picks, shownIds(), id, s.selected) : toggle(s.picks, id) });
}
/** Shift+↑/↓: the range grows or shrinks a row, and the highlight follows its far end. */
export function pickStep(d: number) {
  const picks = step(s.picks, shownIds(), d, s.selected);
  if (picks) update({ picks, selected: picks.end });
}
export const pickAll = () => { const ids = shownIds(); update({ picks: { picked: new Set(ids), anchor: null, end: null, base: new Set(ids) } }); };
export const clearPicks = () => { if (s.picks.picked.size) update({ picks: NO_PICKS }); };

// ---------- the address follows the view ----------
// A new view, or opening/closing an article, is a step in history (Back returns to it);
// moving from one article to the next only rewrites the address.
if (typeof window !== "undefined") {
  const here = () => toPath({ scope: s.scope, filter: s.filter, article: s.reading?.id ?? null, focus: s.focus, settings: s.modal === "settings" ? s.settingsSection : undefined });
  const now = () => ({ scope: scopeKey(s.scope), filter: s.filter, open: !!s.reading, settings: s.modal === "settings" });
  let shown = here();
  let was = now();
  let applying = false;
  listeners.add(() => {
    if (applying) return;
    const path = here();
    if (path === shown) return;
    const is = now();
    // opening Settings is a step too (Back closes it); moving between its sections is not
    const step = is.scope !== was.scope || is.filter !== was.filter || is.open !== was.open || is.settings !== was.settings;
    if (step && !replacingView) history.pushState(null, "", path); else history.replaceState(null, "", path);
    shown = path; was = is;
  });
  addEventListener("popstate", () => {
    viewContext++;
    const place = fromLocation();
    noteNow();
    const sameView = scopeKey(place.scope) === scopeKey(s.scope) && place.filter === s.filter;
    const session = s.reading && place.article && s.reading.session.includes(place.article) ? s.reading.session : place.article ? [place.article] : [];
    const apply = () => {
      applying = true;
      update({
        ...(sameView ? {} : { scope: place.scope, filter: place.filter, keep: new Set<string>() }),
        reading: place.article ? { id: place.article, session, label: s.reading?.label ?? "" } : null,
        selected: place.article ?? s.selected, focus: !!place.focus,
        ...(sectionOf(place.settings) ? { modal: "settings" as const, settingsSection: sectionOf(place.settings)! } : s.modal === "settings" ? { modal: null } : {}),
      });
      applying = false;
      shown = here();
      was = now();
    };
    if (sameView && !!place.article !== !!s.reading) {
      // The browser has already changed its address. Apply history synchronously so
      // another Back/Forward or keyboard gesture cannot act on the old route state.
      asTransition(place.article ? "open" : "close", apply, place.article ?? s.reading?.id, { animate: false });
    } else apply();
  });
}

// Authentication is the lifetime boundary for tab-owned rows and pending UI intents.
onSessionReset(() => {
  clearTimeout(toastTimer); clearTimeout(peekTimer);
  rowCache.clear(); snapshots.clear(); pending.clear(); confirmed.clear(); shown = [];
  update({ reading: null, keep: new Set(), overlay: new Map(), removed: new Map(), selected: null, peek: null, pendingRead: null, picks: NO_PICKS, toast: null, focus: false });
});
